import React, { useState, useRef } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import axios from "axios";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { useTranslation } from "react-i18next";

const Login = ({ setIsAuthenticated }) => {
  const { t } = useTranslation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // ✅ message affiché (déjà traduit)
  const [message, setMessage] = useState("");

  const emailRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const authMotionClass =
    location.state?.authDirection === "toLogin" ? "auth-slide-from-left" : "auth-slide-soft";

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // ✅ backend standard: { status, message_key, message_params?, errors? }
  const translateBackend = (data) => {
    if (!data) return t("msg_unknown_error");

    // 1) message global
    if (data.message_key) {
      return t(data.message_key, data.message_params || {});
    }

    // 2) erreurs (liste de dicts: {field, key, params?})
    if (Array.isArray(data.errors) && data.errors.length) {
      const first = data.errors[0];
      if (first?.key) return t(first.key, first.params || {});
      // fallback si erreurs renvoyées en strings (au cas où)
      if (typeof first === "string") return t(first);
    }

    return t("msg_unknown_error");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // ✅ validations front (messages i18n)
    if (!email || !password) {
      setMessage(t("msg_enter_email_password"));
      setEmail("");
      setPassword("");
      emailRef.current?.focus();
      return;
    }

    if (!emailRegex.test(email)) {
      setMessage(t("msg_invalid_email"));
      setEmail("");
      setPassword("");
      emailRef.current?.focus();
      return;
    }

    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/login_web`, {
        email,
        password,
      });

      if (res?.data?.status === "success") {
        const role = res.data.role || "";
        const application = res.data.application || "";

        // ✅ IMPORTANT : on laisse App gérer localStorage + expiry
        setIsAuthenticated(true, email, role, application);

        setMessage("");
        if (role?.toLowerCase() === "admin") {
          navigate("/admin-dashboard", { replace: true });
        } else {
          navigate("/qr-generator", { replace: true });
        }
      } else {
        setMessage(translateBackend(res?.data));
        setEmail("");
        setPassword("");
        emailRef.current?.focus();
      }
    } catch (err) {
      setMessage(translateBackend(err?.response?.data));
      setEmail("");
      setPassword("");
      emailRef.current?.focus();
    }
  };

  // ✅ Si tu veux garder le style error/info sans regex sur le texte traduit :
  const isError = !!message; // ou basé sur status si tu le stockes
  const messageClass = isError ? "message message--error" : "message message--info";

  return (
    <div className={`auth-shell ${authMotionClass}`}>
      <section className="auth-visual-panel auth-switch-panel">
        <div className="auth-brand-block">
          <span className="auth-brand-mark">A</span>
          <p>{t("assist_by_scan", "ASSIST BY SCAN")}</p>
        </div>

        <div className="auth-orbit" aria-hidden="true">
          <div className="auth-orbit-card auth-orbit-card-main">
            <span className="auth-chip">QR</span>
            <strong>{t("auth_proof_qr", "Machines connectées")}</strong>
            <small>{t("auth_login_story_text", "Connectez-vous pour générer vos QR codes, suivre les demandes de maintenance et retrouver vos rapports en quelques secondes.")}</small>
          </div>
          <div className="auth-orbit-card auth-orbit-card-small auth-orbit-card-top">
            <strong>PDF</strong>
            <small>{t("auth_proof_reports", "Rapports prêts")}</small>
          </div>
          <div className="auth-orbit-card auth-orbit-card-small auth-orbit-card-bottom">
            <strong>24/7</strong>
            <small>{t("auth_proof_access", "Accès terrain")}</small>
          </div>
        </div>

        <div className="auth-visual-copy">
          <p className="auth-eyebrow">{t("login_title", "Login")}</p>
          <h1>{t("auth_login_story_title", "Pilotez vos interventions depuis un espace clair et centralisé.")}</h1>
        </div>
      </section>

      <section className="auth-workspace-panel auth-switch-card">
        <div className="auth-workspace-card">
          <div className="auth-form-head">
            <span>{t("assist_by_scan", "ASSIST BY SCAN")}</span>
            <strong>{t("login_title", "Login")}</strong>
          </div>

          <h2 className="title mb-3">
            {t("login_title", "Login")}
          </h2>

          {message && <p className={messageClass}>{message}</p>}

          <form onSubmit={handleSubmit} className="form">
            <input
              ref={emailRef}
              type="text"
              placeholder={t("email", "Email")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input"
            />

            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder={t("password", "Password")}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="eye-btn"
                aria-label={showPassword ? t("hide_password", "Hide password") : t("show_password", "Show password")}
              >
                {showPassword ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>

            <button type="submit" className="btn btn-lg">
              {t("login", "Login")}
            </button>
          </form>

          <div className="auth-links">
            <Link to="/forgot-password" state={{ authDirection: "toForgot" }}>
              {t("forgot_password", "Forgot password?")}
            </Link>
            <p>
              {t("no_account", "Don't have an account?")}{" "}
              <Link to="/signup" state={{ authDirection: "toSignup" }}>
                {t("signup", "Sign up")}
              </Link>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Login;
